/**
 * Recurring Tickets — shared row / bulk action handlers.
 *
 * Centralises the side-effects triggered by the kebab menu, the detail drawer
 * buttons, and the bulk action bar, so the UI layer stays thin and toasts /
 * dialog opens are fired from a single place (never from hooks — see
 * `feedback_toast_placement.md`).
 */

import { useSetAtom } from "jotai";
import toast from "react-hot-toast";

import { matchPauseResumeConflict } from "controller/api";
import { useResumeRecurringTicket } from "controller";

import {
  activeRecurringTicketIdAtom,
  isDeleteDialogOpenAtom,
  isPauseDialogOpenAtom,
  pendingActionRecurringTicketIdAtom,
} from "../store/recurringTicketAtoms";

interface RecurringTicketActionsApi {
  openDetail: (mappingId: string) => void;
  openEdit: (mappingId: string) => void;
  openPause: (mappingId: string) => void;
  resume: (mappingId: string) => Promise<void>;
  openDelete: (mappingId: string) => void;
  duplicate: (mappingId: string) => void;
  createNow: (mappingId: string) => void;
  openTimeline: (mappingId: string) => void;
}

export const useRecurringTicketActions = (): RecurringTicketActionsApi => {
  const setActiveId = useSetAtom(activeRecurringTicketIdAtom);
  const setPendingActionId = useSetAtom(pendingActionRecurringTicketIdAtom);
  const setPauseDialogOpen = useSetAtom(isPauseDialogOpenAtom);
  const setDeleteDialogOpen = useSetAtom(isDeleteDialogOpenAtom);

  const { mutateAsync: resumeRecurringTicket } = useResumeRecurringTicket();

  const openDetail = (mappingId: string) => {
    setActiveId(mappingId);
  };

  const openEdit = (mappingId: string) => {
    setActiveId(mappingId);
  };

  const openPause = (mappingId: string) => {
    setPendingActionId(mappingId);
    setPauseDialogOpen(true);
  };

  const resume = async (mappingId: string) => {
    try {
      await resumeRecurringTicket({ mappingId });
      toast.success("Recurring schedule resumed successfully");
    } catch (error) {
      // BUG-11: conflict messages come back in `message` not via status code.
      const conflict = matchPauseResumeConflict(
        error as { message?: string; statusCode?: number } | undefined,
      );
      if (conflict === "already_active") {
        toast.error("This recurring ticket is already active.");
        return;
      }
      const message =
        (error as { message?: string } | undefined)?.message ||
        "Could not resume recurring ticket";
      toast.error(message);
    }
  };

  const openDelete = (mappingId: string) => {
    setPendingActionId(mappingId);
    setDeleteDialogOpen(true);
  };

  const duplicate = (mappingId: string) => {
    // Duplicate is pre-filled create; the page owns the drawer open logic.
    setPendingActionId(mappingId);
    toast("Opening duplicate drawer…", { icon: "📋" });
  };

  const createNow = (mappingId: string) => {
    // Create-now mapper lives in Phase 6; placeholder toast for now.
    setPendingActionId(mappingId);
    toast("Create-now is wired via the detail drawer button.", { icon: "ℹ️" });
  };

  const openTimeline = (mappingId: string) => {
    setActiveId(mappingId);
  };

  return {
    openDetail,
    openEdit,
    openPause,
    resume,
    openDelete,
    duplicate,
    createNow,
    openTimeline,
  };
};
